import { SAVE_SCHEMA_VERSION, type GameState } from '../../game-core/src/types.ts';
import { checksum } from './checksum.ts';
import { SAVE_ENVELOPE_VERSION, type SaveEnvelope } from './types.ts';

function hasOnlyFiniteNumbers(value: unknown, seen = new Set<object>()): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (value === null || typeof value !== 'object') return true;
  if (seen.has(value)) return false;
  seen.add(value);
  const valid = Object.values(value).every((child) => hasOnlyFiniteNumbers(child, seen));
  seen.delete(value);
  return valid;
}

export function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<GameState>;
  return state.schemaVersion === SAVE_SCHEMA_VERSION
    && typeof state.playerId === 'string'
    && state.playerId.length > 0
    && typeof state.revision === 'number'
    && state.revision >= 0
    && !!state.run
    && !!state.run.skills
    && typeof state.run.activeSkill === 'string'
    && !!state.run.skills[state.run.activeSkill]
    && (state.run.trainingActive === undefined || typeof state.run.trainingActive === 'boolean')
    && !!state.prestige
    && !!state.stats
    && hasOnlyFiniteNumbers(state);
}

export function encodeSave(state: GameState, writtenAt = Date.now()): string {
  const unsigned = {
    envelopeVersion: SAVE_ENVELOPE_VERSION,
    writtenAt,
    revision: state.revision,
    payload: state
  };
  const digest = checksum(JSON.stringify(unsigned));
  return JSON.stringify({ ...unsigned, checksum: digest });
}

export function decodeSave(raw: string): SaveEnvelope | null {
  try {
    const parsed = JSON.parse(raw) as Partial<SaveEnvelope>;
    if (parsed.envelopeVersion !== SAVE_ENVELOPE_VERSION) return null;
    if (typeof parsed.writtenAt !== 'number' || !Number.isFinite(parsed.writtenAt)) return null;
    if (typeof parsed.revision !== 'number' || !Number.isFinite(parsed.revision)) return null;
    if (typeof parsed.checksum !== 'string' || !isGameState(parsed.payload)) return null;
    const unsigned = {
      envelopeVersion: parsed.envelopeVersion,
      writtenAt: parsed.writtenAt,
      revision: parsed.revision,
      payload: parsed.payload
    };
    if (checksum(JSON.stringify(unsigned)) !== parsed.checksum) return null;
    if (parsed.revision !== parsed.payload.revision) return null;
    return parsed as SaveEnvelope;
  } catch {
    return null;
  }
}

